export function sortByRequired(a: boolean, b: boolean): number {
  return a === b ? 0 : !a ? 1 : -1
}

export function sortBySchema(schema: string, mappings: IntegrationMappingField[]): any[] {
  if (!schema) return []
  const schemaCollection: string[] = schema.trim().split(',')
  return mappings.sort((a, b) => {
    const indexOne = schemaCollection.indexOf(a.fieldName)
    const indexTwo = schemaCollection.indexOf(b.fieldName)
    return indexOne - indexTwo
  })
}

export interface IntegrationMappableField {
  id: number
  type: 'custom_field' | 'integration_field'
  fieldName: string
  displayName?: string
  required: boolean
  fieldTypes?: {
    id: number
    integration_fields_id: number
    type: string
  }[]
}

export interface TemplateMappingOption {
  label: string
  value: string | number
  governance: string
  fieldSourceType: 'field' | 'template_classification'
}

export interface IntegrationMappingField {
  fieldName: string
  type: 'custom_field' | 'integration_field'
  cvFieldId: string | number
  fieldSourceType: 'field' | 'template_classification'
  required: boolean
}

export type DefaultMapping =
  | { claravine: string; outbound: string; type: string }
  | { claravine: string; inbound: string; type: string }

export type DefaultMappings = DefaultMapping[]

export function getMappableFields(
  integrationFields: any,
  customFields: any,
  integrationFieldsFilter?: (field: any) => boolean,
  dataType?: string,
  integrationSlug?: string,
): IntegrationMappableField[] {
  const mappableFields: IntegrationMappableField[] = []

  integrationFields.forEach(i => {
    const mapping: IntegrationMappableField = {
      id: i.id,
      fieldName: i.integration_field_name,
      displayName: getDisplayName(i, 'integration_field_name', dataType, integrationSlug),
      fieldTypes: i.types,
      required: i.required,
      type: 'integration_field',
    }
    if (integrationFieldsFilter) {
      if (integrationFieldsFilter(mapping)) mappableFields.push(mapping)
    } else {
      mappableFields.push(mapping)
    }
  })

  customFields.forEach(i => {
    const mapping: IntegrationMappableField = {
      id: i.id,
      fieldName: i.custom_field_name,
      displayName: getDisplayName(i, 'custom_field_name', dataType, integrationSlug),
      fieldTypes: i.types,
      required: i.required,
      type: 'custom_field',
    }
    if (integrationFieldsFilter) {
      if (integrationFieldsFilter(mapping)) mappableFields.push(mapping)
    } else {
      mappableFields.push(mapping)
    }
  })

  return mappableFields
}

type IMappingIntegration = Record<string, string | number | boolean>

function transformIntegrationFieldMappingStructure(
  mapping: IMappingIntegration,
): IntegrationMappingField {
  // mapping will look like this {foo: 'bar'}
  // the key is the integration field name and the value is claravine field id
  const fieldId = Object.values(mapping)[0]
  const fieldName = Object.keys(mapping)[0]

  const fieldMapping: IntegrationMappingField = {
    cvFieldId: typeof fieldId === 'string' || typeof fieldId === 'number' ? fieldId : '',
    fieldSourceType: 'field',
    fieldName: typeof fieldName === 'string' ? fieldName : '',
    required: false,
    type: 'integration_field',
  }
  return fieldMapping
}

type Mappings = IMappingIntegration | IntegrationMappingField[]

interface IConnectDefaultMappings {
  mappings: IntegrationMappingField[]
  fieldOptions: TemplateMappingOption[]
  defaultMappings: DefaultMappings
  assignmentMetaDataType?: string
}

/**
 * Set default mappings with value from field options to regular mappings.
 * @param args
 * @param args.mappings The mappings.
 * @param args.fieldOptions The field options.
 * @param args.defaultMappings The default mappings to be connected to the mappings obj.
 * @returns {IConnectDefaultMappings} An array of mappings connected by field id to default mappings.
 */
function connectDefaultMappings({
  mappings,
  fieldOptions,
  defaultMappings,
  assignmentMetaDataType,
}: IConnectDefaultMappings) {
  if (!defaultMappings) return mappings

  return mappings.map(mapping => {
    const matchingDefaultMapping = defaultMappings.find(def => {
      if ('outbound' in def)
        return def.outbound === mapping.fieldName && def.type === assignmentMetaDataType
      return def.inbound === mapping.fieldName
    })
    if (!matchingDefaultMapping) return mapping

    const matchingFieldOption = fieldOptions.find(
      option => option.label === matchingDefaultMapping.claravine,
    )

    // If cvFieldId is empty then it has not been set yet; not setting
    // it again, allows for a value change.
    if (matchingFieldOption && mapping.cvFieldId === '') {
      return {
        ...mapping,
        cvFieldId: matchingFieldOption.value,
      }
    }

    return mapping
  })
}

export type FieldMappingType = 'inbound' | 'outbound' | 'supplemental'

export function transformIntegrationFieldMappings(
  mappings: Mappings,
  fieldOptions,
  defaultMappings: DefaultMappings,
  assignmentMetaDataType?: string,
): IntegrationMappingField[] {
  // check whether the mappings structure is an array or an object
  if (Array.isArray(mappings)) {
    // if mappings is an array it should be the correct structure
    return connectDefaultMappings({
      mappings,
      fieldOptions,
      defaultMappings,
      assignmentMetaDataType,
    })
  }

  const fieldMappings: IntegrationMappingField[] = []
  // loop over the field mappings object and transform each key/value pair into type IntegrationMapping
  if (mappings) {
    for (const [key, value] of Object.entries(mappings)) {
      fieldMappings.push(transformIntegrationFieldMappingStructure({ [key]: value }))
    }
  }

  return connectDefaultMappings({
    mappings: fieldMappings,
    fieldOptions,
    defaultMappings,
    assignmentMetaDataType,
  })
}

export type MappingType = 'inbound' | 'outbound' | 'supplemental'

const supplementalFieldsHidden = [
  'cv_id',
  'deep-link',
  'shortened link',
  'custom-bitlink',
  'qr-code',
]
const inboundFieldsHidden = [
  'autonumber',
  'concatenation',
  'pattern',
  'autofill',
  'constant',
  'creative',
]

export const getMappingsToReset = fields => {
  const requiredMappings = {}
  for (const field of fields) {
    if (field.required) {
      let types = field.types.map(t => t.type)
      if (types.length === 0) {
        types = ['default']
      }
      types.map(t => {
        const requiredField = {
          cvFieldId: '',
          fieldSourceType: 'field',
          required: true,
          fieldName: field.integration_field_name,
          type: 'integration_field',
        }
        requiredMappings[t] = [...(requiredMappings[t] || []), requiredField]
      })
    }
  }
  return requiredMappings
}

/*
 * Filter out which supplemental field types should be hidden
 * Based off of their mapping type
 * @param {TemplateMappingOption} templateField
 * @returns a boolean value which determines whether or not the template field should be hidden
 */
const filterSupplementalFields = (templateField: TemplateMappingOption): boolean => {
  const hiddenField = supplementalFieldsHidden.find(f => f == templateField.governance)
  return !hiddenField
}

/*
 * Filter out which inbound field types should be hidden
 * Based off of their mapping type
 * @param {TemplateMappingOption} templateField
 * @returns a boolean value which determines whether or not the template field should be hidden
 */
const filterInboundFields = (templateField: TemplateMappingOption): boolean => {
  const supplementalHiddenFields = supplementalFieldsHidden.find(f => f == templateField.governance)
  const inboundHiddenFields = inboundFieldsHidden.find(f => f == templateField.governance)
  if (supplementalHiddenFields) {
    return false
  }
  if (inboundHiddenFields) {
    return false
  }
  return true
}

/*
 * Filter out template fields that shouldn't be shown
 * Based off of their mapping type
 * @param {MappingType} mappingType
 * @param {TemplateMappingOption} templateField
 * @returns a boolean value which determines whether or not the template field should be filtered
 */
export const filterCVFieldsByIntegrationType = (
  mappingType: MappingType,
  templateField: TemplateMappingOption,
): boolean => {
  switch (mappingType) {
    case 'outbound':
      return true
    case 'supplemental':
      return filterSupplementalFields(templateField)
    case 'inbound':
      return filterInboundFields(templateField)
    default:
      return false
  }
}

const sharedIntegrationFields = {
  'pinterest-inbound': [
    'id',
    'type',
    'status',
    'name',
    'createdTime',
    'updatedTime',
    'summaryStatus',
  ],
  'snapchat-marketing-inbound': [
    'id',
    'createdAt',
    'name',
    'type',
    'startTime',
    'adAccountId',
    'status',
    'deliveryStatus',
  ],
  'facebook-marketing-inbound': [
    'id',
    'name',
    'updatedTime',
    'startTime',
    'createdTime',
    'stopTime',
    'endTime',
  ],
  'facebook-marketing-outbound': [
    'id',
    'name',
    'updatedTime',
    'startTime',
    'createdTime',
    'stopTime',
    'endTime',
  ],
}

const integrationFieldTypeLabels = {
  // Shared
  campaigns: 'Campaign',
  ads: 'Ad',
  // Pinterest
  ad_groups: 'Ad Group',
  ad_accounts: 'Ad Account',
  // SnapChat
  adaccounts: 'Ad Accounts',
  adsquads: 'Ad Squad',
  creatives: 'Creative',
  media: 'Media',
  organizations: 'Organization',
  // Facebook Marketing
  ad_creatives: 'Ad Creative',
  ad_sets: 'Ad Set',
}

/*
 * Because some fields are identified the same in the
 * db across multiple streams, the display name isn't able to be set
 * in the db. In order to add a display name based off of the data type,
 * conditional frontend manipulation is needed.
 * @returns a string of the true display name
 */
const getDisplayName = (
  integrationField: any,
  nameKey: string,
  dataType?: string,
  slug?: string,
) => {
  const displayedName = integrationField.display_name
    ? integrationField.display_name
    : integrationField[nameKey]

  if (!slug) {
    return displayedName
  }

  const sharedFieldKeyExists = Object.keys(sharedIntegrationFields).find(i => i == slug)

  if (
    dataType &&
    sharedFieldKeyExists &&
    sharedIntegrationFields[slug].includes(integrationField[nameKey])
  ) {
    return `${integrationFieldTypeLabels[dataType]} ${displayedName}`
  }
  return displayedName
}
